import { forwardRef, type ReactNode } from 'react';
import { cn } from './cn';

export interface ChipProps {
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
  /**
   * ARIA role. Use 'checkbox' for multi-select, 'radio' for single-select.
   * Defaults to 'checkbox'.
   */
  role?: 'checkbox' | 'radio';
  'aria-label'?: string;
}

/**
 * Interactive chip for multi/single select inputs — Quiet Atlas tokens.
 *
 * - Default: paper bg + ink-100 border, ink-700 text
 * - Pressed: terra-50 bg + terra-600 border, ink-900 text
 * - Disabled: opacity-50, cursor-not-allowed
 *
 * `role="checkbox"` (default) for independent toggles;
 * `role="radio"` for mutually-exclusive options (wrap in role="radiogroup").
 */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      pressed,
      disabled = false,
      onClick,
      children,
      className,
      role = 'checkbox',
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        role={role}
        aria-checked={pressed}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium font-sans',
          'transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-terra-600/30 focus:ring-offset-2 focus:ring-offset-paper',
          pressed
            ? 'border-terra-600 bg-terra-50 text-ink-900 hover:bg-terra-100'
            : 'border-ink-100 bg-paper text-ink-700 hover:border-ink-300 hover:bg-paper-2',
          disabled &&
            'cursor-not-allowed opacity-50 hover:bg-paper hover:border-ink-100',
          className,
        )}
      >
        {children}
      </button>
    );
  },
);
Chip.displayName = 'Chip';
